import { useEffect, useState } from "react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Download, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (url) => url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "/logo-white.png";

/**
 * Compare two semver-ish strings ("1.2.3"). Returns:
 *   -1  if a < b
 *    0  if equal
 *    1  if a > b
 * Missing segments are treated as 0. Non-numeric segments fall back to string compare.
 */
function cmpVersion(a, b) {
  if (!a || !b) return 0;
  const pa = String(a).split(".").map((x) => parseInt(x, 10) || 0);
  const pb = String(b).split(".").map((x) => parseInt(x, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

/**
 * Full-screen blocking gate shown when the installed Android (Capacitor) app
 * version is below the server's configured `min_version`.
 *
 * Only activates inside a Capacitor WebView (window.Capacitor present).
 * The web browser version is unaffected.
 */
export default function AppUpdateGate() {
  const [state, setState] = useState({ checked: false, blocking: false, info: null });

  useEffect(() => {
    let alive = true;

    const check = async () => {
      try {
        // Capacitor exposes isNativePlatform as a function in v3+; some builds
        // surface it as a boolean. Handle both.
        const cap = window.Capacitor;
        const isNative = !!cap && (typeof cap.isNativePlatform === "function" ? cap.isNativePlatform() : !!cap.isNativePlatform);
        if (!isNative) {
          console.info("[AppUpdateGate] Not running inside a Capacitor native app — gate disabled.");
          if (alive) setState({ checked: true, blocking: false, info: null });
          return;
        }

        // Resolve the App plugin without requiring @capacitor/app to be in the
        // frontend's package.json. In a native WebView, the Capacitor bridge
        // automatically injects every plugin under window.Capacitor.Plugins.
        // We try the bridge first; if it fails, fall back to a dynamic import
        // (some builds bundle the plugin into the frontend instead).
        let currentVersion = null;
        let appPlugin = cap?.Plugins?.App || null;
        if (!appPlugin) {
          try {
            const mod = await import(/* webpackIgnore: true */ "@capacitor/app");
            appPlugin = mod?.App || null;
          } catch (e) {
            console.warn("[AppUpdateGate] @capacitor/app not bundled and not on bridge", e);
          }
        }
        try {
          if (appPlugin?.getInfo) {
            const info = await appPlugin.getInfo();
            currentVersion = info?.version || null;
            console.info("[AppUpdateGate] App.getInfo()", info);
          } else {
            console.warn("[AppUpdateGate] App plugin not available — install @capacitor/app or check cap sync");
          }
        } catch (e) {
          console.warn("[AppUpdateGate] App.getInfo() threw", e);
        }
        if (!currentVersion) {
          if (alive) setState({ checked: true, blocking: false, info: null });
          return;
        }

        const r = await fetch(`${API}/app/version`, { credentials: "include" });
        if (!r.ok) {
          console.warn("[AppUpdateGate] /api/app/version request failed", r.status);
          if (alive) setState({ checked: true, blocking: false, info: null });
          return;
        }
        const data = await r.json();
        const blocking = cmpVersion(currentVersion, data.min_version) < 0;
        console.info(
          `[AppUpdateGate] installed=${currentVersion} min_required=${data.min_version} -> blocking=${blocking}`
        );
        if (alive) {
          setState({
            checked: true,
            blocking,
            info: { ...data, currentVersion },
          });
        }
      } catch (e) {
        console.warn("[AppUpdateGate] Unexpected error", e);
        if (alive) setState({ checked: true, blocking: false, info: null });
      }
    };

    check();
    return () => { alive = false; };
  }, []);

  if (!state.checked || !state.blocking) return null;

  const info = state.info || {};

  const openStore = async () => {
    const cap = window.Capacitor;
    const isNative = !!cap && (typeof cap.isNativePlatform === "function" ? cap.isNativePlatform() : !!cap.isNativePlatform);
    if (isNative && info.play_store_url) {
      let browserPlugin = cap?.Plugins?.Browser || null;
      if (!browserPlugin) {
        try {
          const mod = await import(/* webpackIgnore: true */ "@capacitor/browser");
          browserPlugin = mod?.Browser || null;
        } catch { /* noop */ }
      }
      if (browserPlugin?.open) {
        try { await browserPlugin.open({ url: info.play_store_url }); return; }
        catch (e) { console.warn("[AppUpdateGate] Browser.open failed", e); }
      }
    }
    if (info.play_store_url) window.open(info.play_store_url, "_blank");
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-emerald-900 text-white flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      data-testid="app-update-gate"
    >
      <div className="max-w-md w-full text-center space-y-7">
        <img
          src={logoSrc()}
          alt="Super Morsel"
          className="w-24 h-24 object-contain mx-auto drop-shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
        />
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Update Required</h1>
          <p className="mt-3 text-sm text-emerald-100/90 leading-relaxed">
            {info.update_message || "A new version of Super Morsel is available. Please update to continue."}
          </p>
        </div>
        <div className="flex items-center justify-center gap-3 text-[11px] text-emerald-100/70">
          <span>Installed: <b className="text-white">{info.currentVersion}</b></span>
          <span className="opacity-40">·</span>
          <span>Required: <b className="text-white">{info.min_version}</b></span>
        </div>
        <Button
          onClick={openStore}
          className="w-full bg-white text-emerald-900 hover:bg-emerald-50 font-bold rounded-full h-12"
          data-testid="app-update-open-store"
        >
          <Download size={16} className="mr-2" />
          Update Now
          <ExternalLink size={14} className="ml-2 opacity-70" />
        </Button>
        <p className="text-[10px] text-emerald-100/50">
          You must update the app to continue using Super Morsel.
        </p>
      </div>
    </div>
  );
}
